/**
 * A NUMBER WITH ITS STATE. A BARE ZERO IS NEVER CORRECT ON THIS SURFACE.
 *
 * `MetricTile` in the generated types is `{ state, value? }` with three states, and the schema's
 * own words are the specification for this component:
 *
 *   ok                a real figure
 *   no_data           "the range really is empty and zero is true"
 *   not_reported_yet  "the event exists in the vocabulary and no shipped client emits it"
 *
 * The third is what makes this component necessary. Four tiles are `not_reported_yet` AT LAUNCH —
 * shares, average reading time, read-through, traffic sources (saves left 2026-10-05) — no client
 * sends the event. Drawing those as `0` tells an author nobody shared their work, which is a lie
 * the platform would then have to answer a support ticket about. It is "an honest launch state,
 * not a defect", and it is drawn as one.
 *
 * `no_data` STILL PRINTS A ZERO, because there the zero is true. The difference between the two is
 * the whole point and it is carried in the words beside the figure, not in the figure.
 */
import { Card, Group, Stack, Text, Tooltip } from '@mantine/core';
import type { ReactNode } from 'react';
import { i18n, useTranslation } from '../../../i18n';
import { formatCount } from '../format';
import type { MetricState, MetricTile } from '../api';

/**
 * The figure, when there is one. `undefined` for every state and shape that is NOT a real number:
 * `no_data`, `not_reported_yet`, and the `ok`-with-no-value that would otherwise be printed as a
 * zero indistinguishable from a true zero.
 */
export function tileValue(tile: MetricTile): number | undefined {
  return tile.state === 'ok' && tile.value !== undefined && tile.value !== null
    ? tile.value
    : undefined;
}

/** The state's name in the cabinet's language - call it when rendering, never at module load. */
export function metricStateLabel(state: MetricState): string {
  const t = i18n.getFixedT(null, 'cabinet');
  switch (state) {
    case 'ok':
      return t('metric.measured');
    case 'no_data':
      return t('metric.nothingYet');
    case 'not_reported_yet':
      return t('metric.notMeasuredYet');
    default:
      return state;
  }
}

export interface MetricValueProps {
  tile: MetricTile;
  /** Rendered instead of the raw count when the state is `ok` — money, a percentage, a duration. */
  render?: (value: number) => ReactNode;
}

/** Just the value part, for use inside a table cell. */
export function MetricValue({ tile, render }: MetricValueProps) {
  const { t } = useTranslation('cabinet');
  if (tile.state === 'not_reported_yet') {
    return (
      <Tooltip label={t('metric.notReportedYet')} multiline w={280}>
        <Text component="span" size="sm" c="dimmed" style={{ cursor: 'help' }}>
          {t('metric.notMeasuredYet')}
        </Text>
      </Tooltip>
    );
  }
  if (tile.state === 'no_data') {
    return (
      <Text component="span" size="sm">
        0
      </Text>
    );
  }
  const value = tile.value;
  if (value === undefined || value === null) {
    // `ok` with no value is a server fault rather than a state. Say so instead of printing "0",
    // which would be indistinguishable from a true zero.
    return (
      <Text component="span" size="sm" c="dimmed">
        {t('metric.notMeasuredYet')}
      </Text>
    );
  }
  return (
    <Text component="span" size="sm">
      {render === undefined ? formatCount(value) : render(value)}
    </Text>
  );
}

export interface MetricCardProps {
  label: string;
  /** One sentence. What the number means — not a repeat of the label. */
  hint?: string;
  tile: MetricTile;
  render?: (value: number) => ReactNode;
  /** A second line under the figure: "+63 gained, 9 lost", "of 11,907 counted". */
  footnote?: ReactNode;
}

export function MetricCard({ label, hint, tile, render, footnote }: MetricCardProps) {
  const { t } = useTranslation('cabinet');
  const isMissing = tile.state === 'not_reported_yet';
  const value = tile.state === 'ok' ? tile.value : 0;

  return (
    <Card padding="md" component="section" aria-label={label}>
      <Stack gap={4}>
        <Group gap={6} wrap="nowrap" align="baseline">
          <Text
            size="xs"
            fw={700}
            tt="uppercase"
            c="dimmed"
            style={{ letterSpacing: 'var(--mx-ls-eyebrow)' }}
          >
            {label}
          </Text>
        </Group>

        {isMissing ? (
          <Tooltip label={t('metric.notReportedYet')} multiline w={280}>
            <Text size="lg" fw={700} c="dimmed" style={{ cursor: 'help' }}>
              {t('metric.notMeasuredYet')}
            </Text>
          </Tooltip>
        ) : (
          <Text size="xl" fw={800} style={{ fontSize: 'var(--mx-fs-display)', lineHeight: 1.15 }}>
            {value === undefined || value === null
              ? t('metric.notMeasuredYet')
              : render === undefined
                ? formatCount(value)
                : render(value)}
          </Text>
        )}

        {hint === undefined ? null : (
          <Text size="xs" c="dimmed">
            {hint}
          </Text>
        )}
        {footnote}
      </Stack>
    </Card>
  );
}

/**
 * A FIELD THAT IS NOT THERE, rendered rather than hidden.
 *
 * The case this exists for: `distinctReaders` has NO field on `ArticleStats` at all. It is a
 * per-day figure and it does not sum — "a reader who came back on three days would count three
 * times" — so the contract leaves it off a range row rather than serving a number that is
 * silently wrong. THE COLUMN STAYS AND THE ABSENCE IS DRAWN. Dropping the column would leave an
 * author wondering where their reader count went; filling it with the daily figure would be the
 * exact error the omission prevents.
 */
export function AbsentValue({ reason }: { reason: string }) {
  const { t } = useTranslation('cabinet');
  return (
    <Tooltip label={reason} multiline w={300}>
      <Text
        component="span"
        size="sm"
        c="dimmed"
        aria-label={t('metric.absent', { reason })}
        style={{ cursor: 'help' }}
      >
        —
      </Text>
    </Tooltip>
  );
}
